/*
 * Smart search dropdown — Mercado Cosmetics
 * =========================================
 *
 * WHY THIS IS NOT PREDICTIVE SEARCH ANY MORE
 * Shopify's Predictive Search API (/search/suggest) supports 45 buyer locales
 * and Arabic is not one of them. On an Arabic session it returns
 * HTTP 417 "Unsupported buyer locale" before it even looks at the catalogue.
 * That is why this store's dropdown never worked, regardless of theme or
 * settings. This file now uses STOREFRONT search (/search) instead, which has
 * no locale restriction — it is the same engine that already runs when a
 * shopper presses Enter.
 *
 * Storefront search natively indexes title, vendor, product_type, tags and
 * body, so brands like "hudabeauty" and tags like "sunscreen" are searchable
 * without any special field parameters.
 *
 * FIXES CARRIED OVER
 *  - Builds the [data-predictive-search] results container the header template
 *    never rendered (the original null-pointer crash on first keystroke).
 *  - Arabic normalisation + synonym expansion (assets/smart-search-config.js).
 *  - Partial word matching via options[prefix]=last.
 *  - Falls back through alternative interpretations instead of a blank panel.
 *  - Debounce 220ms; cache-key collision bug fixed.
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.productToShow = this.dataset.productToShow || 6;
    this.isOpen = false;
    this.injectedResults = false;
    this.quickSearchTrending = this.querySelectorAll('.quickSearchTrending .link');
    this.searchResultsWidget = this.getElementsByClassName('quickSearchResultsWidget')[0];

    this.ensureResultsContainer();
    if (!this.input) return;

    this.setupEventListeners();
    this.quickSearchTrending.forEach(element => {
      element.addEventListener('click', this.quickSearchTrendingClick.bind(this));
    });
  }

  /* The header template renders the form but not the results panel. */
  ensureResultsContainer() {
    if (!this.predictiveSearchResults) {
      if (getComputedStyle(this).position === 'static') this.style.position = 'relative';

      const box = document.createElement('div');
      box.setAttribute('data-predictive-search', '');
      box.className = 'quickSearchResults smart-search-results';
      box.style.cssText = 'position:absolute;top:calc(100% + 6px);left:0;right:0;z-index:9999;background:#fff;color:#232323;max-height:70vh;overflow-y:auto;box-shadow:0 8px 28px rgba(0,0,0,.14);border-radius:10px;text-align:start;display:none;';

      this.appendChild(box);
      this.predictiveSearchResults = box;
      this.injectedResults = true;
    }

    if (!this.querySelector('.predictive-search-status')) {
      const status = document.createElement('span');
      status.className = 'predictive-search-status';
      status.setAttribute('aria-hidden', 'true');
      status.setAttribute('role', 'status');
      status.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);';
      this.appendChild(status);
    }

    this.statusElement = this.querySelector('.predictive-search-status');
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (form) form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 220).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocClick.bind(this));
    if (this.closeStickySearchButton != null) {
      this.closeStickySearchButton.addEventListener('click', this.onCloseStickySearchClick.bind(this));
    }
    this.input.addEventListener('click', this.doBeforeQuickSearchProduct.bind(this));
    if (window.innerWidth <= 1024 && document.querySelector('[data-search-mobile]')) {
      document.querySelectorAll('[data-search-mobile]').forEach((button) => {
        button.addEventListener('click', this.doBeforeQuickSearchProduct.bind(this));
      });
    }
  }

  /* ----------------------- SMART QUERY LAYER ----------------------- */

  get config() {
    return window.SmartSearchConfig || { SYNONYMS: {} };
  }

  normalize(term) {
    return (term || '')
      .toLowerCase()
      .replace(/[ً-ٰٟ]/g, '')
      .replace(/[آأإٱ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/ى/g, 'ي')
      .replace(/ـ/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  expandQuery(term) {
    const synonyms = this.config.SYNONYMS || {};
    const normalized = this.normalize(term);
    if (synonyms[normalized]) return synonyms[normalized];

    const words = normalized.split(' ');
    let changed = false;
    const mapped = words.map((word) => {
      if (synonyms[word]) { changed = true; return synonyms[word]; }
      return word;
    });

    return changed ? mapped.join(' ') : normalized;
  }

  buildCandidates(term) {
    const raw = this.normalize(term).slice(0, 64);
    const candidates = [];
    const push = (value) => {
      if (value && value.length && !candidates.includes(value)) candidates.push(value);
    };

    push(this.expandQuery(raw));
    push(raw);

    const words = raw.split(' ').filter(Boolean);
    if (words.length > 1) {
      push(words.slice(0, -1).join(' '));
      push(words.slice().sort((a, b) => b.length - a.length)[0]);
    }

    return candidates;
  }

  /* Storefront search. tier 0 uses prefix matching; tier 1 is the plain
     query, which is the most permissive thing Shopify will always accept. */
  buildSearchUrl(term, tier) {
    const params = new URLSearchParams();
    params.set('q', term);
    params.set('type', 'product');
    if (tier === 0) params.set('options[prefix]', 'last');
    params.set('section_id', 'quick-search-results');
    return `${routes.search_url}?${params.toString()}`;
  }

  extractSection(text) {
    const section = new DOMParser()
      .parseFromString(text, 'text/html')
      .querySelector('#shopify-section-quick-search-results');
    return section ? section.innerHTML.trim() : '';
  }

  fetchMarkup(term) {
    const tryTier = (tier) => {
      if (tier > 1) return Promise.resolve('');

      return fetch(this.buildSearchUrl(term, tier))
        .then((response) => {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then((text) => {
          const markup = this.extractSection(text);
          return markup ? markup : tryTier(tier + 1);
        })
        .catch(() => tryTier(tier + 1));
    };

    return tryTier(0);
  }

  hasResults(markup) {
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    return doc.querySelectorAll('.products-grid .product').length > 0;
  }

  buildDidYouMean(original, used) {
    if (this.normalize(original) === this.normalize(used)) return '';
    const safe = String(used).replace(/[<>"']/g, '');
    return '<div class="smart-search-note" style="padding:8px 14px;font-size:13px;opacity:.75;">Showing results for <strong>' + safe + '</strong></div>';
  }

  emptyState(term) {
    const safe = String(term).replace(/[<>"']/g, '');
    const href = `${routes.search_url}?q=${encodeURIComponent(term)}&type=product`;
    return '<div style="padding:18px 16px;font-size:14px;">No quick matches for <strong>'
      + safe + '</strong>. <a href="' + href + '" style="text-decoration:underline;">See all results</a></div>';
  }

  /* ----------------------- FLOW ----------------------- */

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const candidates = this.buildCandidates(searchTerm);

    const attempt = (index, fallbackMarkup) => {
      if (index >= candidates.length) {
        const finalMarkup = fallbackMarkup || this.emptyState(searchTerm);
        this.cachedResults[queryKey] = finalMarkup;
        this.renderSearchResults(finalMarkup);
        return;
      }

      const candidate = candidates[index];

      return this.fetchMarkup(candidate)
        .then((markup) => {
          if (markup && this.hasResults(markup)) {
            const finalMarkup = this.buildDidYouMean(searchTerm, candidate) + markup;
            this.cachedResults[queryKey] = finalMarkup;
            this.renderSearchResults(finalMarkup);
            return;
          }
          return attempt(index + 1, fallbackMarkup || markup);
        })
        .catch(() => attempt(index + 1, fallbackMarkup));
    };

    attempt(0, null);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
    this.hideTrendingAndProducts();

    if ((window.show_multiple_currencies && typeof Currency != 'undefined' && Currency.currentCurrency != window.shop_currency) || window.show_auto_currency) {
      Currency.convertAll(window.shop_currency, $('#currencies .active').attr('data-currency'), 'span.money', 'money_format');
    }
    window.sharedFunctions?.swapHoverVideoProductCard();
  }

  setLiveRegionLoadingState() {
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';
    setTimeout(() => {
      if (this.statusElement) this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const counter = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (counter) this.setLiveRegionText(counter.textContent);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) { this.close(true); return; }
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) return this.showTrendingAndProducts();

    if (this.getAttribute('results') === 'true') this.open();
    else this.getSearchResults(searchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      this.showTrendingAndProducts();
    }

    switch (event.code) {
      case 'ArrowUp':   event.preventDefault(); this.switchOption('up'); break;
      case 'ArrowDown': event.preventDefault(); this.switchOption('down'); break;
      case 'Enter':     this.selectOption(); break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('.products-grid .product');
    let activeElement = allElements[0];

    if (moveUp && !selectedElement) return;
    if (!allElements.length) return;

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) activeElement = selectedElement.nextElementSibling || allElements[0];
    else if (moveUp) activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];

    if (!activeElement || activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selectedProduct) selectedProduct.click();
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    if (this.injectedResults) this.predictiveSearchResults.style.display = 'block';
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);

    if (this.predictiveSearchResults) {
      if (this.injectedResults) this.predictiveSearchResults.style.display = 'none';
      else this.predictiveSearchResults.removeAttribute('style');
    }

    this.isOpen = false;
  }

  doBeforeQuickSearchProduct(){
    this.renderQuickSearchProductBlock();
  }

  renderQuickSearchProductBlock(){
    if (!this.searchResultsWidget) return;
    if (this.searchResultsWidget.classList.contains('ajax-loaded')) return;

    const block = this.searchResultsWidget.querySelector('.quickSearchProduct');
    const url = block?.getAttribute('data-collection');
    const limit = block?.getAttribute('data-limit');

    if (url == null || url === '') { this.searchResultsWidget.classList.add('ajax-loaded'); return; }

    $.ajax({
      type: 'GET',
      url: window.routes.root + '/collections/' + url,
      cache: false,
      data: { view: 'ajax_product_block', constraint: `limit=${limit}+sectionId=list-result-block` },
      beforeSend: () => { block.closest('.quickSearchResultsWidget').classList.add('ajax-loaded'); },
      success: (data) => {
        block.querySelector('.products-grid').innerHTML = this.handleResponse(data, limit);
        window.sharedFunctions?.swapHoverVideoProductCard();
      }
    });
  }

  handleResponse(data, limit) {
    let htmlMedia = document.createElement('div');
    htmlMedia.innerHTML = data;
    $(htmlMedia).find(`.product:nth-child(n+${Number(limit) + 1})`).remove();
    return $(htmlMedia).html();
  }

  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return
    this.trendingAndProductsBlock.classList.add('is-show')
    this.trendingAndProductsBlock.classList.remove('hidden');
  }

  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return
    this.trendingAndProductsBlock.classList.remove('is-show')
    this.trendingAndProductsBlock.classList.add('hidden');
  }

  onDocClick(e) {
    const isInModal = this.contains(e.target), $target = e.target
    if (!isInModal || $target.closest('.header-search-popup-close') || $target.matches('.header-search-popup-close') || $target.closest('.header-search-close') || $target.matches('.header-search-close')) {
      this.close();
      this.hideTrendingAndProducts();
    }
  }

  onCloseStickySearchClick() {
    this.close();
    this.hideTrendingAndProducts();
    document.querySelector('body').classList.remove('sticky-search-open');
    document.querySelector('body').classList.remove('sticky-search-menu-open');
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-open')
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-custom-open')
  }

  quickSearchTrendingClick(event) {
    window.location = event.currentTarget.getAttribute('href');
  }
}

customElements.define('predictive-search', PredictiveSearch);
